import React, { useCallback, useMemo, useState } from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import { Redirect, useHistory } from "react-router-dom";
import SelectClass from "../ChildComponent/SelectClass";
import BackButton from "../ChildComponent/BackButton";
import myToast from "../Utils/MyToast";
import "../css/Admin_css/admin-customize.css";

const ACCENT = COLORS.card_red || "#ef4444";

const DUE_ITEMS = [
  {
    id: "current_session",
    label: "Current Session Due",
    icon: "fa-calendar-check-o",
    variant: "current",
    getAmount: (pay) =>
      Number(pay?.current_session_due || 0) +
      Number(pay?.current_session_service_fee || 0),
  },
  {
    id: "previous_session",
    label: "Previous Session Due",
    icon: "fa-history",
    variant: "prev",
    getAmount: (pay) =>
      Number(pay?.previous_session_due || 0) +
      Number(pay?.previous_session_service_fee || 0),
  },
  {
    id: "accessories",
    label: "Accessories",
    icon: "fa-shopping-bag",
    variant: "accessories",
    getAmount: (pay) => Number(pay?.total_accessories_fee || 0),
  },
  {
    id: "old_due",
    label: "Old Due (Before App)",
    icon: "fa-archive",
    variant: "old",
    getAmount: (pay) => Number(pay?.student_old_due || 0),
  },
];

function formatAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("en-IN");
}

function getStudentInitial(name) {
  const trimmed = String(name || "").trim();
  return (trimmed[0] || "?").toUpperCase();
}

function getClassLabel(student) {
  const className = student.class_name || "—";
  const sectionName = student.section_name;
  if (!sectionName) return className;
  return `${className} · ${sectionName}`;
}

function getPaySummary(student) {
  return student?.pay_summary?.[0] || {};
}

function getStudentDueTotal(pay) {
  return DUE_ITEMS.reduce((sum, item) => sum + item.getAmount(pay), 0);
}

function StudentAvatar({ name, imagePath }) {
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = Boolean(imagePath) && !imgFailed;

  return (
    <div className="cd-avatar-wrap">
      {showImage ? (
        <img
          src={imagePath}
          alt={name || "Student"}
          className="cd-avatar"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span className="cd-avatar-initial" aria-hidden="true">
          {getStudentInitial(name)}
        </span>
      )}
    </div>
  );
}

const CurrentDue = () => {
  const history = useHistory();
  const [loggedIn] = useState(!!localStorage.getItem("token"));
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [dueDetails, setDueDetails] = useState([]);

  const fetchCurrentDue = useCallback(async (class_id, section_id, session_id) => {
    setLoadingStudent(true);
    setDueDetails([]);

    try {
      const schoolCode = SessionManager.getSchoolCode();
      const url =
        process.env.REACT_APP_SERVER_NAME +
        process.env.REACT_APP_TARGET_DIR +
        "getClassStudentCurrentDue.php";

      const response = await axios.get(url, {
        params: {
          school_code: schoolCode,
          class_id,
          section_id,
          session_id,
        },
      });

      if (response.data.api_status === "OK") {
        setDueDetails(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        myToast.notify_warning(
          response.data.api_remark || "No current due records found."
        );
      }
    } catch (error) {
      console.error("CurrentDue fetch error:", error);
      myToast.notify_error("An error occurred while fetching current due details.");
    } finally {
      setLoadingStudent(false);
    }
  }, []);

  const handleClassSelect = useCallback(
    (class_id, section_id, session_id) => {
      fetchCurrentDue(class_id, section_id, session_id);
    },
    [fetchCurrentDue]
  );

  const visibleStudents = useMemo(
    () =>
      dueDetails.filter((student) => getStudentDueTotal(getPaySummary(student)) > 0),
    [dueDetails]
  );

  const totalAmount = useMemo(
    () =>
      visibleStudents.reduce(
        (sum, student) => sum + getStudentDueTotal(getPaySummary(student)),
        0
      ),
    [visibleStudents]
  );

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <div className="content" style={{ background: COLORS.white }}>
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <a href="#">Current Due</a>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton onClick={() => history.goBack()} />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow cd-page"
        style={{ "--cd-accent": ACCENT, "--mMargin": "50px" }}
      >
        <SelectClass action={handleClassSelect} selected_position="0" />

        {loadingStudent ? (
          <div className="cd-loader-wrap">
            <MyLoader />
          </div>
        ) : visibleStudents.length === 0 ? (
          <div className="cd-empty">
            <div className="cd-empty-icon">
              <i className="fa fa-users" aria-hidden="true" />
            </div>
            <div className="cd-empty-title">No records found</div>
            <div className="cd-empty-text">
              Select a class to view current due details.
            </div>
          </div>
        ) : (
          <>
            <div className="cd-summary-banner">
              <div className="cd-summary-left">
                <span className="cd-summary-icon">
                  <i className="fa fa-calculator" aria-hidden="true" />
                </span>
                <div>
                  <div className="cd-summary-label">Class Total Current Due</div>
                  <div className="cd-summary-amount">₹{formatAmount(totalAmount)}</div>
                </div>
              </div>
              <div className="cd-summary-count">
                {visibleStudents.length} student
                {visibleStudents.length !== 1 ? "s" : ""}
              </div>
            </div>

            <div className="cd-list">
              {visibleStudents.map((item, index) => {
                const pay = getPaySummary(item);
                const studentTotal = getStudentDueTotal(pay);

                return (
                  <div
                    key={`${item.student_enrollment_no || "student"}-${index}`}
                    className="cd-card"
                  >
                    <div className="cd-card-top">
                      <div className="cd-card-main">
                        <StudentAvatar
                          name={item.stu_name}
                          imagePath={item.student_img_path}
                        />
                        <div className="cd-info">
                          <div className="cd-name">{item.stu_name || "—"}</div>
                          {item.father_name && (
                            <div className="cd-father">
                              <i className="fa fa-user" aria-hidden="true" />
                              {item.father_name}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="cd-meta cd-meta-center">
                        <span className="cd-meta-chip cd-meta-chip-class">
                          <span className="cd-chip-icon">
                            <i className="fa fa-graduation-cap" aria-hidden="true" />
                          </span>
                          <span className="cd-chip-text">{getClassLabel(item)}</span>
                        </span>
                        <span className="cd-meta-chip cd-meta-chip-roll">
                          <span className="cd-chip-icon">
                            <i className="fa fa-hashtag" aria-hidden="true" />
                          </span>
                          <span className="cd-chip-text">
                            Roll {item.student_roll_no ?? "—"}
                          </span>
                        </span>
                        {item.student_enrollment_no && (
                          <span className="cd-meta-chip cd-meta-chip-enroll">
                            <span className="cd-chip-icon">
                              <i className="fa fa-id-card" aria-hidden="true" />
                            </span>
                            <span className="cd-chip-text">
                              {item.student_enrollment_no}
                            </span>
                          </span>
                        )}
                      </div>

                      <div className="cd-total-chip">
                        <span className="cd-total-icon" aria-hidden="true">
                          <i className="fa fa-inr" />
                        </span>
                        <div className="cd-total-body">
                          <div className="cd-total-label">Total Current Due</div>
                          <div className="cd-total-value">
                            ₹{formatAmount(studentTotal)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="cd-fees-row">
                      {DUE_ITEMS.map((dueItem) => {
                        const amount = dueItem.getAmount(pay);

                        return (
                          <div
                            key={dueItem.id}
                            className={`cd-fee-chip cd-fee-chip--${dueItem.variant}`}
                          >
                            <span className="cd-fee-icon">
                              <i className={`fa ${dueItem.icon}`} aria-hidden="true" />
                            </span>
                            <div className="cd-fee-body">
                              <div className="cd-fee-label">{dueItem.label}</div>
                              <div className="cd-fee-value">₹{formatAmount(amount)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <style>{`
        .cd-page {
          padding-bottom: 20px;
          background: #ffffff !important;
        }
        .cd-loader-wrap {
          display: flex;
          justify-content: center;
          padding: 40px 0;
        }
        .cd-empty {
          text-align: center;
          padding: 48px 20px;
          color: #6b7280;
        }
        .cd-empty-icon {
          width: 56px;
          height: 56px;
          margin: 0 auto 14px;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          color: #9ca3af;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
        }
        .cd-empty-title {
          font-size: 16px;
          font-weight: 700;
          color: #374151;
          margin-bottom: 6px;
        }
        .cd-empty-text {
          font-size: 13px;
        }
        .cd-summary-banner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin: 16px 0 14px;
          padding: 14px 16px;
          border-radius: 14px;
          background: linear-gradient(135deg, var(--cd-accent) 0%, ${COLORS.primary_color_shade7 || "#0288D1"} 100%);
          color: #fff;
          border: none;
          box-shadow: 0 4px 16px color-mix(in srgb, var(--cd-accent) 35%, transparent);
        }
        .cd-summary-left {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }
        .cd-summary-icon {
          width: 42px;
          height: 42px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.2);
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          flex-shrink: 0;
        }
        .cd-summary-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: rgba(255, 255, 255, 0.92);
        }
        .cd-summary-amount {
          font-size: 24px;
          font-weight: 800;
          line-height: 1.2;
          margin-top: 2px;
        }
        .cd-summary-count {
          font-size: 12px;
          font-weight: 700;
          padding: 6px 12px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.18);
          border: 1px solid rgba(255, 255, 255, 0.28);
          white-space: nowrap;
        }
        .cd-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .cd-card {
          border-radius: 14px;
          background: #ffffff;
          border: 1px solid ${COLORS.grey3 || "#E0E0E0"};
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          overflow: hidden;
          transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
        }
        .cd-card:hover {
          transform: translateY(-1px);
          border-color: color-mix(in srgb, var(--cd-accent) 40%, #e5e7eb);
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
        }
        .cd-card-top {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
          align-items: center;
          gap: 12px 16px;
          padding: 12px 14px;
        }
        .cd-card-main {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
          justify-self: start;
        }
        .cd-avatar-wrap {
          width: 60px;
          height: 60px;
          border-radius: 16px;
          overflow: hidden;
          flex-shrink: 0;
          border: 2px solid color-mix(in srgb, var(--cd-accent) 25%, #e5e7eb);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          background: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cd-avatar {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .cd-avatar-initial {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: 800;
          color: var(--cd-accent);
          background: color-mix(in srgb, var(--cd-accent) 12%, #ffffff);
        }
        .cd-info {
          min-width: 0;
        }
        .cd-name {
          font-size: 15px;
          font-weight: 800;
          color: ${COLORS.black8 || "#333"};
          margin-bottom: 4px;
        }
        .cd-father {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #6b7280;
        }
        .cd-father .fa {
          font-size: 11px;
          color: #9ca3af;
        }
        .cd-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .cd-meta-center {
          justify-content: center;
          justify-self: center;
        }
        .cd-meta-chip {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          max-width: 100%;
          height: 32px;
          box-sizing: border-box;
          padding: 0 10px 0 5px;
          border-radius: 999px;
          border: 1px solid transparent;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
        }
        .cd-chip-icon {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 10px;
        }
        .cd-chip-text {
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .cd-meta-chip-class {
          background: color-mix(in srgb, ${COLORS.primary_color_shade8 || "#0277BD"} 9%, #ffffff);
          border-color: color-mix(in srgb, ${COLORS.primary_color_shade8 || "#0277BD"} 22%, #e5e7eb);
          color: ${COLORS.primary_color_shade8 || "#0277BD"};
        }
        .cd-meta-chip-class .cd-chip-icon {
          background: color-mix(in srgb, ${COLORS.primary_color_shade8 || "#0277BD"} 16%, #ffffff);
          border: 1px solid color-mix(in srgb, ${COLORS.primary_color_shade8 || "#0277BD"} 20%, #e5e7eb);
        }
        .cd-meta-chip-roll {
          background: color-mix(in srgb, var(--cd-accent) 10%, #ffffff);
          border-color: color-mix(in srgb, var(--cd-accent) 26%, #e5e7eb);
          color: var(--cd-accent);
        }
        .cd-meta-chip-roll .cd-chip-icon {
          background: color-mix(in srgb, var(--cd-accent) 18%, #ffffff);
          border: 1px solid color-mix(in srgb, var(--cd-accent) 24%, #e5e7eb);
        }
        .cd-meta-chip-roll .cd-chip-text {
          font-size: 14px;
          font-weight: 800;
        }
        .cd-meta-chip-enroll {
          background: #f8fafc;
          border-color: #e2e8f0;
          color: #64748b;
        }
        .cd-meta-chip-enroll .cd-chip-icon {
          background: #ffffff;
          color: #94a3b8;
          border: 1px solid #e2e8f0;
        }
        .cd-total-chip {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          min-width: 132px;
          padding: 8px 12px 8px 8px;
          border-radius: 999px;
          background: color-mix(in srgb, var(--cd-accent) 10%, #ffffff);
          border: 1px solid color-mix(in srgb, var(--cd-accent) 24%, #e5e7eb);
          box-shadow: 0 2px 8px color-mix(in srgb, var(--cd-accent) 12%, rgba(15, 23, 42, 0.06));
          justify-self: end;
        }
        .cd-total-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 700;
          color: #fff;
          background: linear-gradient(135deg, var(--cd-accent) 0%, ${COLORS.primary_color_shade7 || "#0288D1"} 100%);
          flex-shrink: 0;
        }
        .cd-total-body {
          text-align: right;
        }
        .cd-total-label {
          font-size: 9px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.07em;
          color: #94a3b8;
        }
        .cd-total-value {
          font-size: 17px;
          font-weight: 800;
          color: var(--cd-accent);
          line-height: 1.15;
        }
        .cd-fees-row {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 8px;
          padding: 0 14px 12px;
          border-top: 1px solid #f1f5f9;
          padding-top: 10px;
        }
        .cd-fee-chip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 10px 6px 6px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          background: #ffffff;
          min-width: 0;
        }
        .cd-fee-icon {
          width: 28px;
          height: 28px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          flex-shrink: 0;
        }
        .cd-fee-label {
          font-size: 9px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #94a3b8;
          line-height: 1.2;
        }
        .cd-fee-value {
          font-size: 13px;
          font-weight: 800;
          line-height: 1.15;
          margin-top: 1px;
        }
        .cd-fee-chip--current {
          border-color: color-mix(in srgb, var(--cd-accent) 24%, #e5e7eb);
          background: color-mix(in srgb, var(--cd-accent) 6%, #ffffff);
        }
        .cd-fee-chip--current .cd-fee-icon {
          background: color-mix(in srgb, var(--cd-accent) 14%, #ffffff);
          color: var(--cd-accent);
        }
        .cd-fee-chip--current .cd-fee-value {
          color: var(--cd-accent);
        }
        .cd-fee-chip--prev {
          border-color: color-mix(in srgb, #f59e0b 24%, #e5e7eb);
          background: color-mix(in srgb, #f59e0b 6%, #ffffff);
        }
        .cd-fee-chip--prev .cd-fee-icon {
          background: color-mix(in srgb, #f59e0b 14%, #ffffff);
          color: #d97706;
        }
        .cd-fee-chip--prev .cd-fee-value {
          color: #d97706;
        }
        .cd-fee-chip--accessories {
          border-color: color-mix(in srgb, #8b5cf6 24%, #e5e7eb);
          background: color-mix(in srgb, #8b5cf6 6%, #ffffff);
        }
        .cd-fee-chip--accessories .cd-fee-icon {
          background: color-mix(in srgb, #8b5cf6 14%, #ffffff);
          color: #7c3aed;
        }
        .cd-fee-chip--accessories .cd-fee-value {
          color: #7c3aed;
        }
        .cd-fee-chip--old {
          border-color: color-mix(in srgb, #64748b 24%, #e5e7eb);
          background: #f8fafc;
        }
        .cd-fee-chip--old .cd-fee-icon {
          background: #ffffff;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
        .cd-fee-chip--old .cd-fee-value {
          color: #475569;
        }
        @media (max-width: 768px) {
          .cd-card-top {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          .cd-card-main,
          .cd-meta-center,
          .cd-total-chip {
            width: 100%;
            justify-self: stretch;
          }
          .cd-meta-center {
            justify-content: center;
          }
          .cd-total-chip {
            justify-content: space-between;
          }
          .cd-summary-banner {
            flex-direction: column;
            align-items: flex-start;
          }
          .cd-fees-row {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .cd-fee-chip {
            min-width: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default CurrentDue;
